import { useId } from 'react';
import { motion } from 'motion/react';
import { useReducedMotion } from '../motion/useReducedMotion';

export type MascotMood =
  | 'happy'
  | 'excited'
  | 'thinking'
  | 'sad'
  | 'shy'
  | 'celebrate'
  | 'sleepy'
  | 'surprised';

export type MascotProps = {
  /** Humeur — pilote yeux, bouche, pattes et accessoires. */
  mood?: MascotMood;
  /** Taille (px ou valeur CSS). Défaut 96. */
  size?: number | string;
  /** Idle animé (clignement + balancement de queue). Coupé si reduced-motion. */
  animated?: boolean;
  className?: string;
  /** Si fourni → `role="img"` + label. Sinon la mascotte est décorative (aria-hidden). */
  'aria-label'?: string;
};

/*
 * Palette d'ILLUSTRATION de Redpan (fixe, indépendante du thème : la fourrure
 * reste rousse en clair comme en sombre). Ce ne sont pas des tokens d'UI.
 */
const FUR_LIGHT = '#FF7A45';
const FUR_DARK = '#E8430D';
const CREAM = '#FFF3E6';
const DARK = '#35201A'; // brun très sombre : pupilles, nez, pattes
const EAR_DARK = '#4A2718';
const BLUSH = '#FF9E70';
const TOE = '#FFC49B';
const STAR = '#FACC15';
const TEAR = '#38BDF8';

const OPEN_EYE_MOODS: ReadonlySet<MascotMood> = new Set([
  'happy',
  'excited',
  'thinking',
  'sad',
  'surprised',
]);

/**
 * Redpan, la mascotte panda ROUX (SVG inline, aucune image externe).
 * Tête ronde, oreilles sombres à intérieur crème, masque facial crème,
 * queue annelée roux/crème. Décorative par défaut (`aria-hidden`).
 */
export function Mascot({
  mood = 'happy',
  size = 96,
  animated = true,
  className,
  'aria-label': ariaLabel,
}: MascotProps) {
  const uid = useId();
  const furId = `redpan-fur-${uid}`;
  const reduced = useReducedMotion();
  const animate = animated && !reduced;
  const eyesOpen = OPEN_EYE_MOODS.has(mood);

  return (
    <svg
      viewBox="0 0 128 128"
      width={size}
      height={size}
      className={className}
      data-mood={mood}
      role={ariaLabel ? 'img' : undefined}
      aria-label={ariaLabel}
      aria-hidden={ariaLabel ? undefined : true}
      focusable="false"
    >
      <defs>
        <radialGradient id={furId} cx="0.5" cy="0.35" r="0.85">
          <stop offset="0%" stopColor={FUR_LIGHT} />
          <stop offset="100%" stopColor={FUR_DARK} />
        </radialGradient>
      </defs>

      {/* Queue annelée roux/crème, balancement doux en idle */}
      <motion.g
        style={{ transformBox: 'fill-box', transformOrigin: '15% 90%' }}
        animate={animate ? { rotate: [-3, 4, -3] } : undefined}
        transition={
          animate ? { duration: 3.2, repeat: Infinity, ease: 'easeInOut' } : undefined
        }
      >
        <circle cx="98" cy="100" r="13" fill={FUR_DARK} />
        <circle cx="107" cy="89" r="12" fill={CREAM} />
        <circle cx="113" cy="77" r="11" fill={FUR_DARK} />
        <circle cx="117" cy="65" r="10" fill={CREAM} />
        <circle cx="119" cy="55" r="8.5" fill={FUR_DARK} />
      </motion.g>

      {/* Oreilles arrondies sombres, intérieur crème */}
      <g>
        <circle cx="34" cy="30" r="13" fill={EAR_DARK} />
        <circle cx="34" cy="31" r="7.5" fill={CREAM} />
        <circle cx="94" cy="30" r="13" fill={EAR_DARK} />
        <circle cx="94" cy="31" r="7.5" fill={CREAM} />
      </g>

      {/* Tête ronde, fourrure rousse en dégradé */}
      <circle cx="64" cy="68" r="40" fill={`url(#${furId})`} />

      {/* Masque facial crème : sourcils/joues + museau */}
      <g fill={CREAM}>
        <ellipse cx="46" cy="60" rx="11.5" ry="14" />
        <ellipse cx="82" cy="60" rx="11.5" ry="14" />
        <ellipse cx="64" cy="88" rx="19" ry="15" />
      </g>

      {/* Joues rousses */}
      <g fill={BLUSH} opacity="0.8">
        <ellipse cx="33" cy="77" rx="5.5" ry="3.5" />
        <ellipse cx="95" cy="77" rx="5.5" ry="3.5" />
      </g>

      {/* Yeux — variations par humeur */}
      {eyesOpen && (
        <motion.g
          style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
          animate={animate ? { scaleY: [1, 0.15, 1] } : undefined}
          transition={
            animate
              ? {
                  duration: 0.22,
                  times: [0, 0.5, 1],
                  repeat: Infinity,
                  repeatDelay: 3.4,
                  ease: 'easeInOut',
                }
              : undefined
          }
        >
          {mood === 'thinking' ? (
            <>
              <circle cx="49" cy="60" r="5.5" fill={DARK} />
              <circle cx="84" cy="60" r="5.5" fill={DARK} />
              <circle cx="51" cy="58" r="1.8" fill="#FFFFFF" />
              <circle cx="86" cy="58" r="1.8" fill="#FFFFFF" />
            </>
          ) : (
            <>
              <circle
                cx="47"
                cy="63"
                r={mood === 'excited' || mood === 'surprised' ? 7.5 : 6}
                fill={DARK}
              />
              <circle
                cx="81"
                cy="63"
                r={mood === 'excited' || mood === 'surprised' ? 7.5 : 6}
                fill={DARK}
              />
              <circle cx="45" cy="60.5" r="2.2" fill="#FFFFFF" />
              <circle cx="79" cy="60.5" r="2.2" fill="#FFFFFF" />
              {mood === 'excited' && (
                <>
                  <circle cx="50" cy="65" r="1.2" fill="#FFFFFF" />
                  <circle cx="84" cy="65" r="1.2" fill="#FFFFFF" />
                </>
              )}
            </>
          )}
        </motion.g>
      )}
      {mood === 'celebrate' && (
        <g stroke={DARK} strokeWidth="3" strokeLinecap="round" fill="none">
          <path d="M40 63 Q47 56 54 63" />
          <path d="M74 63 Q81 56 88 63" />
        </g>
      )}
      {mood === 'sleepy' && (
        <g stroke={DARK} strokeWidth="3" strokeLinecap="round" fill="none">
          <path d="M40 63 Q47 68 54 63" />
          <path d="M74 63 Q81 68 88 63" />
        </g>
      )}
      {mood === 'sad' && (
        <>
          <g stroke={DARK} strokeWidth="2.5" strokeLinecap="round">
            <path d="M40 56 L53 51" />
            <path d="M88 56 L75 51" />
          </g>
          <path d="M88 70 q4 6 0 9 q-4 -3 0 -9" fill={TEAR} />
        </>
      )}

      {/* Nez + bouche */}
      <ellipse cx="64" cy="81" rx="5" ry="4" fill={DARK} />
      <circle cx="62.5" cy="79.8" r="1.2" fill="#FFFFFF" opacity="0.7" />
      {mood === 'happy' && (
        <path
          d="M56 91 Q64 98 72 91"
          stroke={DARK}
          strokeWidth="3"
          strokeLinecap="round"
          fill="none"
        />
      )}
      {mood === 'thinking' && (
        <path d="M59 93 L68 92" stroke={DARK} strokeWidth="3" strokeLinecap="round" fill="none" />
      )}
      {(mood === 'excited' || mood === 'celebrate') && (
        <>
          <path d="M55 90 Q64 103 73 90 Z" fill={DARK} />
          <path d="M60 94 Q64 99 68 94 Q64 101 60 94" fill={FUR_DARK} />
        </>
      )}
      {mood === 'sad' && (
        <path
          d="M56 96 Q64 89 72 96"
          stroke={DARK}
          strokeWidth="3"
          strokeLinecap="round"
          fill="none"
        />
      )}
      {(mood === 'shy' || mood === 'sleepy') && (
        <path
          d="M61 92 Q64 95 67 92"
          stroke={DARK}
          strokeWidth="2.5"
          strokeLinecap="round"
          fill="none"
        />
      )}
      {mood === 'surprised' && <ellipse cx="64" cy="94" rx="4.5" ry="5.5" fill={DARK} />}

      {/* Moustaches (points) */}
      <g fill={DARK} opacity="0.55">
        <circle cx="50" cy="86" r="1" />
        <circle cx="47" cy="90" r="1" />
        <circle cx="78" cy="86" r="1" />
        <circle cx="81" cy="90" r="1" />
      </g>

      {/* Pattes devant les yeux (shy) */}
      {mood === 'shy' && (
        <g>
          <ellipse cx="46" cy="62" rx="12.5" ry="10.5" fill={EAR_DARK} stroke={CREAM} strokeWidth="1.5" />
          <ellipse cx="82" cy="62" rx="12.5" ry="10.5" fill={EAR_DARK} stroke={CREAM} strokeWidth="1.5" />
          <g fill={TOE}>
            <circle cx="40" cy="56.5" r="2.2" />
            <circle cx="46" cy="55" r="2.2" />
            <circle cx="52" cy="56.5" r="2.2" />
            <circle cx="76" cy="56.5" r="2.2" />
            <circle cx="82" cy="55" r="2.2" />
            <circle cx="88" cy="56.5" r="2.2" />
          </g>
        </g>
      )}

      {/* Bras levés + étoiles (celebrate) */}
      {mood === 'celebrate' && (
        <g>
          <path d="M32 66 L19 53" stroke={EAR_DARK} strokeWidth="8" strokeLinecap="round" />
          <path d="M96 66 L109 53" stroke={EAR_DARK} strokeWidth="8" strokeLinecap="round" />
          <circle cx="18" cy="51" r="7" fill={EAR_DARK} stroke={CREAM} strokeWidth="1.5" />
          <circle cx="110" cy="51" r="7" fill={EAR_DARK} stroke={CREAM} strokeWidth="1.5" />
          <g fill={STAR}>
            <path d="M18 18 l2.2 4.6 4.8 0.6 -3.5 3.4 0.9 4.8 -4.4 -2.4 -4.4 2.4 0.9 -4.8 -3.5 -3.4 4.8 -0.6 Z" />
            <path d="M104 8 l1.8 3.8 4 0.5 -2.9 2.8 0.7 4 -3.6 -2 -3.6 2 0.7 -4 -2.9 -2.8 4 -0.5 Z" />
            <path d="M64 6 l1.5 3.2 3.4 0.4 -2.5 2.4 0.6 3.4 -3 -1.7 -3 1.7 0.6 -3.4 -2.5 -2.4 3.4 -0.4 Z" />
          </g>
        </g>
      )}

      {/* Bulles de réflexion (thinking) */}
      {mood === 'thinking' && (
        <g fill={CREAM} stroke={DARK} strokeWidth="1.2">
          <circle cx="103" cy="34" r="3" />
          <circle cx="110" cy="24" r="4.5" />
          <circle cx="119" cy="12" r="6" />
        </g>
      )}

      {/* Zzz (sleepy) */}
      {mood === 'sleepy' && (
        <g
          fill="none"
          stroke={CREAM}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M100 34 h8 l-8 8 h8" />
          <path d="M112 18 h6 l-6 6 h6" strokeWidth="2" />
        </g>
      )}
    </svg>
  );
}
